'use client';

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import dynamic from 'next/dynamic';

import { getIsAppUser } from '@ps/functions/hooks/useIsAppUser';
import { nativeShare } from '@ps/functions/native-bridge/bridge';

/**
 * Lazy-loaded: the modal (Radix Dialog + share UI) only mounts after the first
 * `openShare(...)`, so it stays out of the first-load bundle of every surface
 * that mounts `ShareDialogProvider` — notably the public bio, whose layout
 * mounts the provider. `ssr: false` because it never renders on first paint
 * (it's gated behind `state`, set only by a client interaction).
 */
const ShareModal = dynamic(
  () => import('./ShareModal').then(mod => mod.ShareModal),
  { ssr: false },
);

interface OpenShareInput {
  url: string;
  title?: string;
  heading?: string;
  description?: string;
  shareText?: string;
}

interface ShareDialogContextValue {
  openShare: (input: OpenShareInput) => void;
  closeShare: () => void;
}

const ShareDialogContext = createContext<ShareDialogContextValue | null>(null);

/**
 * Mounts the single, app-wide `ShareModal` and exposes
 * `openShare(...)` to every descendant via context. Drop this
 * provider once at the app root (or as low as the bio root) —
 * every caller in the tree can then open the modal without
 * mounting it locally.
 *
 * Pattern mirrors `sonner` toasts and Radix `DialogProvider`:
 * one shared mount, many open calls. Avoids the previous
 * "11 instances of `<ShareDialog />`" sprinkled across feature
 * components.
 */
export function ShareDialogProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<OpenShareInput | null>(null);

  const openShare = useCallback((input: OpenShareInput) => {
    setState(input);
  }, []);
  const closeShare = useCallback(() => setState(null), []);

  const value = useMemo(
    () => ({ openShare, closeShare }),
    [openShare, closeShare],
  );

  return (
    <ShareDialogContext.Provider value={value}>
      {children}
      {state ? (
        <ShareModal
          open
          onOpenChange={next => {
            if (!next) setState(null);
          }}
          url={state.url}
          title={state.title}
          heading={state.heading}
          description={state.description}
          shareText={state.shareText}
        />
      ) : null}
    </ShareDialogContext.Provider>
  );
}

/**
 * Imperative opener for the global share modal. Returns
 * `openShare` and `closeShare`; the modal itself is mounted
 * once by `ShareDialogProvider`, so callers never render
 * `<ShareDialog />` JSX anymore.
 *
 * **Native app:** this hook is the ONE place that branches web-modal vs. OS
 * share sheet. Every share surface (PDP, affiliate/external products, bio
 * store + My Life cards, series/clip players, live, channels, contact card)
 * already funnels through `openShare`, so app users get the phone's native
 * sheet everywhere from this single branch — no per-surface changes.
 *
 * The branch runs INSIDE the event handler, never during render, so nothing
 * about SSR/hydration changes for the bio tree: the server output, the
 * hydrated markup, and the lazy `ShareModal` chunk are all untouched, and the
 * modal stays the fallback whenever the shell isn't actually reachable.
 *
 * Falls back to a no-op + console warning when used outside a
 * provider — keeps non-bio surfaces (storybook, isolated
 * tests) callable without forcing every caller to wrap their
 * mount tree.
 */
export function useShareDialog(): ShareDialogContextValue {
  const ctx = useContext(ShareDialogContext);

  return useMemo(() => {
    const openModal =
      ctx?.openShare ??
      ((input: OpenShareInput) => {
        console.warn(
          '[useShareDialog] No ShareDialogProvider in tree — share dropped',
          input.url,
        );
      });

    return {
      openShare: (input: OpenShareInput) => {
        // Fold the URL into the message — Android's share sheet sends only
        // `message` and drops `url`, so a bare `shareText` would share no link.
        const message = input.shareText
          ? `${input.shareText}\n${input.url}`
          : undefined;
        if (
          getIsAppUser() &&
          nativeShare({ url: input.url, title: input.title, message })
        ) {
          return;
        }
        openModal(input);
      },
      closeShare: ctx?.closeShare ?? (() => {}),
    };
  }, [ctx]);
}
